import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Loader2, FileCheck2, ChevronRight, ChevronDown, CheckCircle2, Eye, Circle,
  FolderLock, FolderOpen, AlertTriangle, Search,
} from "lucide-react";

const TEAL = "#2f6f6b";

function StatCard({ label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`read-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone || "text-slate-800"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function StatusPill({ status }) {
  if (status === "acknowledged")
    return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"><CheckCircle2 className="h-3 w-3" />Acknowledged</span>;
  if (status === "viewed")
    return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5"><Eye className="h-3 w-3" />Viewed</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5"><Circle className="h-3 w-3" />Unread</span>;
}

function DocRow({ d }) {
  const [open, setOpen] = useState(false);
  const pct = d.expected ? Math.round((d.viewed / d.expected) * 100) : 0;
  return (
    <div className="border-t border-slate-100" data-testid={`read-doc-${d.doc_id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 py-2 px-1 text-left hover:bg-slate-50">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
        {d.kind === "external" ? <FolderOpen className="h-4 w-4 text-sky-500 shrink-0" /> : <FolderLock className="h-4 w-4 text-emerald-600 shrink-0" />}
        <span className="text-sm text-slate-800 flex-1 truncate">{d.title}</span>
        <span className="text-[11px] text-slate-500">
          {d.viewed}/{d.expected} read {d.acknowledged ? `· ${d.acknowledged} ack` : ""}
        </span>
        <span className={`text-[11px] font-semibold w-10 text-right ${pct === 100 ? "text-emerald-600" : pct === 0 ? "text-slate-400" : "text-amber-600"}`}>{pct}%</span>
      </button>
      {open && (
        <div className="pb-2 pl-9 pr-1 space-y-1">
          {d.workers.length === 0 && <div className="text-[11px] text-slate-400 py-1">No workers currently in this participant's access scope.</div>}
          {d.workers.map((w) => (
            <div key={w.worker} className="flex items-center justify-between py-1" data-testid={`read-worker-${d.doc_id}-${w.worker}`}>
              <span className="text-xs text-slate-600">{w.worker_name}</span>
              <StatusPill status={w.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ParticipantCard({ p }) {
  const [open, setOpen] = useState(false);
  const pct = p.expected ? Math.round((p.viewed / p.expected) * 100) : 0;
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={`read-participant-${p.participant}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 p-4 text-left">
        {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-800 truncate">{p.participant_name}</div>
          <div className="text-[11px] text-slate-500">{p.docs.length} document{p.docs.length === 1 ? "" : "s"} · {p.expected} expected read{p.expected === 1 ? "" : "s"}</div>
        </div>
        {p.expected > 0 && p.viewed < p.expected && (
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
            <AlertTriangle className="h-3 w-3" />{p.expected - p.viewed} outstanding
          </span>
        )}
        <div className="w-28 shrink-0">
          <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${pct === 100 ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
          </div>
          <div className="text-[10px] text-slate-400 text-right mt-0.5">{pct}% read</div>
        </div>
      </button>
      {open && <div className="px-4 pb-2">{p.docs.map((d) => <DocRow key={`${d.kind}:${d.doc_id}`} d={d} />)}</div>}
    </div>
  );
}

export default function DocumentReadReport() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.documentReadReport()
      .then(setData)
      .catch(() => { setData({ error: true }); toast.error("Could not load the read report."); });
  }, []);

  const filtered = useMemo(() => {
    if (!data?.participants) return [];
    const term = q.trim().toLowerCase();
    if (!term) return data.participants;
    return data.participants.filter((p) => (p.participant_name || "").toLowerCase().includes(term));
  }, [data, q]);

  if (!data) return <div className="flex justify-center pt-24"><Loader2 className="h-6 w-6 animate-spin" style={{ color: TEAL }} /></div>;
  if (data.error) return <div className="p-6 text-sm text-slate-500">Could not load the report.</div>;

  const t = data.totals;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="document-read-report">
      <div className="flex items-center gap-2 mb-1">
        <FileCheck2 className="h-5 w-5" style={{ color: TEAL }} />
        <h1 className="text-xl font-bold text-slate-800 tracking-tight">Document Read Report</h1>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Read/unread status of every worker-visible document against each worker who has that participant in their access scope. "Read" means opened at least once; a green tick means the worker acknowledged it.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Expected reads" value={t.expected} sub={`${t.participants} participants`} />
        <StatCard label="Read" value={`${t.view_pct}%`} sub={`${t.viewed} of ${t.expected}`} tone="text-sky-600" />
        <StatCard label="Acknowledged" value={`${t.ack_pct}%`} sub={`${t.acknowledged} of ${t.expected}`} tone="text-emerald-600" />
        <StatCard label="Outstanding" value={t.outstanding} sub="not yet opened" tone={t.outstanding ? "text-amber-600" : "text-slate-800"} />
      </div>

      {data.outstanding_workers.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 mb-6" data-testid="outstanding-workers">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-slate-800">Workers with outstanding reads</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.outstanding_workers.slice(0, 30).map((w) => (
              <span key={w.worker} className="inline-flex items-center gap-1.5 text-xs bg-white border border-amber-200 rounded-full px-2.5 py-1" data-testid={`outstanding-worker-${w.worker}`}>
                <span className="text-slate-700">{w.worker_name}</span>
                <span className="text-amber-700 font-semibold">{w.outstanding}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter participants…" className="pl-9 h-9 text-sm" data-testid="read-report-search" />
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && <div className="text-sm text-slate-400 py-8 text-center">No participants match.</div>}
        {filtered.map((p) => <ParticipantCard key={p.participant} p={p} />)}
      </div>
    </div>
  );
}
